import React from "react";
import { Card, Row, Col, Typography, Spin, Button } from "antd";
import Chart from "react-apexcharts";
import {
  BookOpen01Icon,
  Tick01Icon,
  Ticket01Icon,
  Time01Icon,
} from "hugeicons-react";
import { ApexOptions } from "apexcharts";
import { TicketService } from "./Service/TicketService";
import { TicketStats } from "./Type/Ticket";

const { Title, Text } = Typography;

const cardColors = {
  primary: "#232b3e",
  accent: "#b6c3e2",
  white: "#fff",
  iconPurple: "#8C89F8",
  iconBlue: "#7BD3EA",
  grayBg: "#F0F2F5",
};

function prettyMinutes(min?: number): string {
  if (min === undefined) return "-";
  if (min >= 1440) return `${Math.floor(min / 1440)} gün ${Math.round((min % 1440) / 60)} sa`;
  if (min >= 60) return `${Math.floor(min / 60)} sa ${min % 60} dk`;
  return `${min} dk`;
}

export default function TicketWidget() {
  const [stats, setStats] = React.useState<TicketStats | null>(null);
  const [loading, setLoading] = React.useState(true);

  // Hem ilk açılışta, hem de butonla manuel çağırmak için:
  const fetchStats = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await TicketService.getTicketStats();
      setStats(data);
    } catch {
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // İlk açılışta otomatik çağır (useEffect ile)
  React.useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const chartOptions: ApexOptions = {
    chart: {
      type: "radialBar" as const,
      sparkline: { enabled: true },
    },
    plotOptions: {
      radialBar: {
        startAngle: -110,
        endAngle: 110,
        hollow: { size: "64%" },
        track: {
          background: cardColors.grayBg,
          strokeWidth: "70%",
          margin: 4,
        },
        dataLabels: {
          show: true,
          name: { show: false },
          value: {
            fontSize: "2rem",
            fontWeight: 800,
            show: true,
            offsetY: 12,
            color: cardColors.primary,
            fontFamily: "'Urbanist', sans-serif",
            formatter: (val: number) => `${val}%`,
          },
        },
      },
    },
    colors: ["#333e48"],
    stroke: { lineCap: "round" },
    labels: ["Tamamlandı"],
  };

  if (loading) return <Spin />;

  return (
    <Card
      style={{
        borderRadius: 18,
        background: cardColors.white,
        boxShadow: "0 4px 18px #232b3e12",
        minWidth: 380,
        minHeight: 260
      }}
    >
      <Row gutter={[16,16]} justify={"space-between"} align={"top"}>
        <Col>
          <Title
            level={2}
            style={{
              marginTop: 0,
              marginBottom: 0,
              color: cardColors.primary,
              fontWeight: 800,
              fontSize: 40,
              lineHeight: 1,
            }}
          >
            {stats?.totalTicket ?? "-"} 
          </Title>
          <Text
            style={{
              color: "#7A8699",
              fontSize: 18,
              fontWeight: 600,
              letterSpacing: 0.1,
            }}
          >
            Toplam Destek Talebi
          </Text>
          <div style={{ marginTop: 48 }}>
            <Row align="middle" style={{ marginBottom: 8 }}>
              <Col>
                <BookOpen01Icon
                  size={20}
                  color={cardColors.iconBlue}
                  style={{ marginRight: 8 }}
                />
              </Col>
              <Col>
                <Text style={{ color: cardColors.primary, fontWeight: 600 }}>
                  {stats?.openedTicket ?? "-"}
                </Text>
                <Text style={{ color: "#888", marginLeft: 8 }}>Açık</Text>
              </Col>
            </Row>
            <Row align="middle" style={{ marginBottom: 8 }}>
              <Col>
                <Tick01Icon
                  size={20}
                  color={cardColors.iconPurple}
                  style={{ marginRight: 8 }}
                />
              </Col>
              <Col>
                <Text style={{ color: cardColors.primary, fontWeight: 600 }}>
                  {stats?.closedTicket ?? "-"}
                </Text>
                <Text style={{ color: "#888", marginLeft: 8 }}>Kapalı</Text>
              </Col>
            </Row>
            <Row align="middle">
              <Col>
                <Time01Icon
                  size={20}
                  color={"#333e48"}
                  style={{ marginRight: 8 }}
                />
              </Col>
              <Col>
                <Text style={{ color: cardColors.primary, fontWeight: 600 }}>
                  {prettyMinutes(stats?.responseTimeMin)}
                </Text>
                <Text style={{ color: "#888", marginLeft: 8 }}>Yanıt Süresi</Text>
              </Col>
            </Row>
          </div>
        </Col>
        <Col>
          <Chart
            options={chartOptions}
            series={[stats?.closedRatio ?? 0]}
            type="radialBar"
            height={250}
          />
        </Col>
      </Row>
    </Card>
  );
}
